import { safety as safetyApi } from './api.js'
import { crisisResourcesOf } from './config.js'
import { crisisContent } from './screens/SafetyScreen.jsx'

/**
 * Turn a safety outcome from POST /api/answers into a crisis screen.
 *
 * ONE implementation, used by BOTH answering paths. Day 0 and the beep are the
 * only two places a participant can disclose, and they had one wiring between
 * them: the beep read `result.safety` and day 0 did not, so a woman answering
 * "Yes, quite often" to "the thought of harming myself has occurred to me" on
 * day 0 saw "That's everything for today. Thank you."
 *
 * The rule had fired. The row was written. The response carried the outcome.
 * The screen dropped it. That is why this lives in one place now rather than
 * being implemented twice and diverging again.
 *
 * It was invisible until config closed the engine half, because until then no
 * day-0 item could produce an outcome at all and the field was always null.
 * Nobody introduced it; it was waiting.
 *
 * @param {number|string} level the level the SERVER assigned. Never computed here.
 * @param {object} bundle       the config bundle, for the offline fallback
 * @returns {Promise<object|null>} content for SafetyScreen, or null if there is
 *   genuinely nothing to show — which must never be treated as "no outcome".
 */
export async function resolveCrisis(level, bundle) {
  if (!level) return null

  try {
    // The server resolves country and displayable state; the client renders it.
    const payload = await safetyApi.resources()
    const content = crisisContent(payload, level)
    if (content) return content
  } catch {
    /* fall through to the bundle */
  }

  // A crisis screen must never fail to appear because a fetch did. The copy is
  // config's, reviewed, and never claims anyone has been notified.
  //
  // No matched_country is passed, deliberately: offline we cannot know whose
  // country this is, so the state resolves to `none` and the copy promises
  // nothing — the only honest reading when we are blind.
  const crisis = crisisResourcesOf(bundle)
  return (
    crisisContent(
      { levels: crisis?.levels, resources: crisis?.default?.resources ?? [] },
      level,
    ) ?? {
      state: 'none',
      level: { title: '', body: '', cta: 'Continue', show_resources: false },
      resources: [],
      countryName: '',
      dismissalRequired: true,
    }
  )
}

/**
 * Fold a newly-raised outcome into whatever is already on screen.
 *
 * TWO THINGS ARE BEING KEPT AT ONCE and they pull in different directions:
 *
 *  1. THE HIGHEST LEVEL WINS. Two disclosures must never resolve to the
 *     gentler screen, and a level never downgrades.
 *  2. EVERY ANSWER THAT CONTRIBUTED IS STILL ACKNOWLEDGED. Rule 1 suppresses
 *     the lower outcome's SCREEN, and if its answer id went with it, that
 *     answer's safety_events would stay NULL forever — indistinguishable from
 *     "she was never shown resources", which is the exact gap #132 closes.
 *     The screen she is shown is at least as severe as the one suppressed, so
 *     it genuinely covered both, and saying so is the honest record.
 *
 * This is reachable: a day-0 grid saves several items in flight at once, so a
 * second outcome can land while the first is still on screen.
 */
export function mergeOutcome(prev, next) {
  if (!prev) return next
  const answerIds = [...new Set([...(prev.answerIds ?? []), ...(next.answerIds ?? [])])]
  return prev.rank >= next.rank ? { ...prev, answerIds } : { ...next, answerIds }
}

/**
 * Tell the server she was shown the screen. ONE call per answer that raised it.
 *
 * FIRE AND FORGET, and the swallowed rejection is the specification, not
 * laziness (#132, #119): nothing blocks on this, no label changes, and a
 * failure must never re-show the crisis screen or start a retry loop. The
 * participant has just dismissed a crisis screen; the worst possible response
 * to a failed POST is to put it back in front of her.
 */
export function acknowledgeSafety(answerIds) {
  for (const id of answerIds ?? []) {
    if (id === null || id === undefined) continue
    safetyApi.acknowledge(id).catch(() => {})
  }
}
